import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../api/client";

// GET /api/v1/admin/plannings/ → TODAS las clases de la academia (panel del
// propietario). El endpoint es owner-only; la agenda del docente (/plannings/)
// sigue filtrada a sus propias clases.
function useAllPlannings() {
  return useQuery({
    queryKey: ["all-plannings"],
    queryFn: () => apiClient.get("/api/v1/admin/plannings/").then((r) => r.data),
  });
}

// PATCH /api/v1/plannings/<id>/ desde el panel del dueño (reasignar instructor,
// mover fecha/hora, etc.). Invalida tanto la vista global como las agendas.
function useAdminUpdatePlanning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.patch(`/api/v1/plannings/${id}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-plannings"] });
      qc.invalidateQueries({ queryKey: ["plannings"] });
    },
  });
}

// Reprograma/reasigna cualquier clase por arrastre desde el calendario global del
// dueño. `patch` es lo que va al backend (date/time_start/duration/instructor);
// `optimistic` es cómo se refleja en la caché al instante (incluye el objeto
// instructor resuelto para recolorear/recolumnar el evento). Revierte si falla.
function useAdminReschedulePlanning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) =>
      apiClient.patch(`/api/v1/plannings/${id}/`, patch).then((r) => r.data),
    onMutate: async ({ id, optimistic }) => {
      await qc.cancelQueries({ queryKey: ["all-plannings"] });
      const prev = qc.getQueryData(["all-plannings"]);
      qc.setQueryData(["all-plannings"], (old) =>
        (old ?? []).map((p) => (p.id === id ? { ...p, ...optimistic } : p))
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["all-plannings"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["all-plannings"] });
      qc.invalidateQueries({ queryKey: ["plannings"] });
    },
  });
}

// DELETE /api/v1/plannings/<id>/ desde el panel del dueño. El backend solo lo
// permite al propietario, bloquea clases calificadas y notifica/retira inscritos.
function useAdminDeletePlanning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      apiClient.delete(`/api/v1/plannings/${id}/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-plannings"] });
      qc.invalidateQueries({ queryKey: ["plannings"] });
    },
  });
}

export {
  useAllPlannings,
  useAdminUpdatePlanning,
  useAdminReschedulePlanning,
  useAdminDeletePlanning,
};
